import { Platform, PlatformModule } from '@angular/cdk/platform';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, Input, NgModule, OnDestroy, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { CUSTOMERENVIRONMENT, WINDOW } from '@app/common/app-tokens';
import { Utilities } from '@app/common/utilities';
import { ActionButtonModule } from '@app/components/actions/action-button/action-button.component';
import { ActionLinkModule } from '@app/components/actions/action-link/action-link.component';
import { ErrorTemplatesModule } from '@app/components/error-templates/error-templates.component';
import { UploadDocumentButtonModule } from '@app/components/my-documents/upload-document/upload-document-button/upload-document-button.component';
import { TextPlaceholderModule } from '@app/components/text-placeholder/text-placeholder.component';
import { WidgetPlaceholderModule } from '@app/components/widget-placeholder/widget-placeholder.component';
import { MyDocument } from '@app/models/my-document';
import { LinkModule } from '@app/directives/link.directive';
import { MyDocumentsService } from '@app/services/my-documents.service';
import { OneDriveService } from '@app/services/onedrive.service';
import { Environment } from '@environments/interface.environment';
import { FileSystemDirectoryEntry, FileSystemFileEntry, NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';
import { UploadDocumentModalComponent } from '../upload-document/upload-document-modal/upload-document-modal.component';
import { UploadDocumentWarningMessageComponent } from '../upload-document/upload-document-warning-message/upload-document-warning-message.component';
import { FormatDateModule } from '@app/pipes/format-date.pipe';

@Component({
  selector: 'app-my-documents',
  templateUrl: './my-documents.component.html',
  styleUrls: ['./my-documents.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MyDocumentsComponent implements OnInit, OnDestroy {

  @Input() numberOfRecentDocuments = 10;
  @Input() numberOfTrendingDocuments = 10;
  @Input() numberOfSharedDocuments = 10;
  onedriveLink: string;
  private versionIE: number;
  loading = true;
  myRecentDocuments$: Observable<MyDocument[]>;
  myTrendingDocuments$: Observable<MyDocument[]>;
  mySharedResult$: Observable<MyDocument[]>;
  onedriveLink$: Observable<string>;
  private errorSubject$ = new Subject();
  @Input() showMore = 5;
  @Input() minDocuments = 5;
  @Input() maxDocuments = 10;
  selectedIndex = 0;
  private originalShowMoreValue: number;
  private dialogRef: MatDialogRef<UploadDocumentModalComponent>;
  fileSizeLimit = 4194304;
  private subscription: Subscription = new Subscription();
  isMobileDevice = false;
  componentName = 'Your documents';

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              @Inject(WINDOW) private window: Window,
              private renderer2: Renderer2,
              private myDocumentsService: MyDocumentsService,
              private onedriveService: OneDriveService,
              private cdRef: ChangeDetectorRef,
              public dialog: MatDialog,
              private platform: Platform) {
    this.originalShowMoreValue = this.showMore;
    this.isMobileDevice = this.platform.ANDROID || this.platform.IOS;
  }

  getUrl(url: string) {
    return Utilities.getRealUrlFromTokenBasedUrl(this.environment, url);
  }

  handleChange(event: MatTabChangeEvent) {
    this.loading = true;
    this.showMore = this.originalShowMoreValue;
    this.cdRef.detectChanges();
  }

  toggleShowMore() {
    this.showMore = this.showMore === this.minDocuments ? this.maxDocuments : this.minDocuments;
  }

  getUrlAsBreadCrumbs(url) {
    const a = this.renderer2.createElement('a');
    a.setAttribute('href', url);

    const escapedPathName = unescape(a.pathname);
    const paths = escapedPathName.split('/');

    if (a.pathname.endsWith('.aspx')) {
      paths.pop();
    }

    if (a.pathname.startsWith('/')) {
      paths.shift();
    }

    const breadcrumbUrl = [a.hostname].concat(paths).join(' > ');
    a.remove();
    return breadcrumbUrl;
  }

  ngOnInit() {
    this.versionIE = Utilities.detectIE(this.window.navigator.userAgent);

    this.myRecentDocuments$ = this.myDocumentsService.getMyRecentDocuments(this.numberOfRecentDocuments)
      .pipe(map(result => {
        return result.filter(doc => {
          return doc.resourceVisualization.title;
        }).map(doc => {
          return MyDocument.fromUsedInsight(doc, this.versionIE);
        });
      }), tap(() => {
        this.loading = false;
        this.cdRef.detectChanges();
      }),
        catchError(err => {
          this.errorSubject$.next(err);
          return of(null);
        }));

    this.myTrendingDocuments$ = this.myDocumentsService.getMyTrendingDocuments(this.numberOfTrendingDocuments)
      .pipe(map(result => {
        return result.filter(doc => {
          return doc.resourceVisualization.title;
        }).map(doc => {
          return MyDocument.fromTrendingDocuments(doc, this.versionIE);
        });
      }), tap(() => {
        this.loading = false;
        this.cdRef.detectChanges();
      }),
        catchError(err => {
          this.errorSubject$.next(err);
          return of(null);
        }));

    this.mySharedResult$ = this.myDocumentsService.getMySharedDocuments(this.numberOfSharedDocuments)
      .pipe(map(result => {
        return result.filter(doc => {
          return doc.resourceVisualization.title;
        }).map(doc => {
          return MyDocument.fromSharedInsight(doc, this.versionIE);
        });
      }), tap(() => {
        this.loading = false;
        this.cdRef.detectChanges();
      }),
        catchError(err => {
          this.errorSubject$.next(err);
          return of(null);
        }));

    this.onedriveLink$ = this.onedriveService
      .getOneDrive()
      .pipe(
        map(result => result.webUrl));
  }

  // drag and drop functionality
  dropped(files: NgxFileDropEntry[]) {

    for (const droppedFile of files) {
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;

        fileEntry.file((file: File) => {
          if (!file) {
            return;
          }

          const reader = new FileReader();

          // check filesize - max limit is 4MB
          if (file.size > this.fileSizeLimit) {
            const warningDialogRef = this.dialog.open(UploadDocumentWarningMessageComponent, {});
            warningDialogRef.componentInstance.onedriveLink = this.onedriveLink;
          } else {
            reader.onload = () => {
              const rawData = reader.result;

              // open dialog
              this.dialogRef = this.dialog.open(UploadDocumentModalComponent, {});

              // listens for cancel and override event from dialog in case the user wants to stop/replace the upload
              this.subscription.add(
                this.dialogRef.componentInstance.emitData
                  .pipe(switchMap(userEvent => {
                    if (userEvent.cancel) {
                      return of(null);
                    } else if (userEvent.override) {
                      return this.onedriveService.uploadFile(file.name, rawData);
                    } else {
                      return this.onedriveService.getFile(file.name).pipe(concatMap(res => {
                        // Document already exists in one drive
                        if (res && res.id) {
                          return of('doc exists');
                        } else if (res === 404) {
                          return this.onedriveService.uploadFile(file.name, rawData);
                        } else {
                          return of('error');
                        }
                      }));
                    }
                  }))
                  .subscribe(result => {
                    if (result && result !== 'doc exists' && result !== 'error') {
                      this.dialogRef.componentInstance.fileUrl = result.webUrl;
                      this.dialogRef.componentInstance.uploading = false;
                      this.dialogRef.componentInstance.docExists = false;
                    } else if (result === 'doc exists') {
                      this.dialogRef.componentInstance.docExists = true;
                    } else {
                      console.log('Error status' + result);
                      this.dialogRef.componentInstance.docExists = false;
                      this.dialogRef.componentInstance.uploading = true;
                      this.dialogRef.componentInstance.error = true;
                    }
                  }));
            };

            reader.readAsArrayBuffer(file);
          }

        });
      } else {
        const fileEntry = droppedFile.fileEntry as FileSystemDirectoryEntry;
        console.log(droppedFile.relativePath, fileEntry);
      }
    }
  }

  ngOnDestroy(): void {
    this.subscription && this.subscription.unsubscribe();
  }

}
@NgModule({
  declarations: [MyDocumentsComponent],
  exports: [MyDocumentsComponent],
  imports: [
    CommonModule,
    PlatformModule,
    MatTabsModule,
    MatTableModule,
    ActionButtonModule,
    ActionLinkModule,
    NgxFileDropModule,
    TextPlaceholderModule,
    ErrorTemplatesModule,
    WidgetPlaceholderModule,
    UploadDocumentButtonModule,
    LinkModule,
    FormatDateModule
  ]
})
export class MyDocumentsModule { }
